import { Controller } from "@hotwired/stimulus"
import { getString, setString } from "../utils/local_storage"

export default class extends Controller {
  static targets = ["tab", "panel"]
  static values = {
    activeClass: { type: String, default: "active" },
    inactiveClass: { type: String, default: "hidden" },
    defaultTab: { type: String, default: "" },
    storageKey: { type: String, default: "tabs_active_index" }
  }

  connect() {
    // Check for stored active tab index in localStorage if storageKey is provided
    if (this.hasStorageKeyValue) {
      const storedIndex = getString(this.storageKeyValue)
      if (storedIndex !== null) {
        const tabIndex = parseInt(storedIndex, 10)
        if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex < this.tabTargets.length) {
          this.activateTabByIndex(tabIndex)
          return
        }
      }
    }

    // Check URL hash for tab ID
    const hash = window.location.hash.replace('#', '')
    if (hash) {
      const tabWithId = this.tabTargets.find(tab => tab.id === hash)
      if (tabWithId) {
        this.activateTab(tabWithId)
        return
      }
    }

    // Set default tab on connect if specified
    if (this.defaultTabValue) {
      const defaultTab = this.tabTargets.find(tab => tab.id === this.defaultTabValue)
      if (defaultTab) {
        this.activateTab(defaultTab)
        return
      }
    }

    // Otherwise activate the first tab
    if (this.tabTargets.length > 0) {
      this.activateTab(this.tabTargets[0])
    }
  }

  // Handle click event on a tab
  change(event) {
    event.preventDefault()
    this.activateTab(event.currentTarget)
  }

  // Handle data-action="tabs#select" with data-index
  select(event) {
    event.preventDefault()
    const index = parseInt(event.currentTarget.getAttribute('data-index'), 10)
    if (!isNaN(index) && index >= 0 && index < this.tabTargets.length) {
      this.activateTabByIndex(index)
    }
  }

  // Activate tab by index
  activateTabByIndex(index) {
    if (index >= 0 && index < this.tabTargets.length) {
      this.activateTab(this.tabTargets[index])

      // Store active tab index in localStorage if storageKey is provided
      if (this.hasStorageKeyValue) {
        localStorage.setItem(this.storageKeyValue, index.toString())
      }
    }
  }

  // Activate a specific tab element
  activateTab(tab) {
    const tabIndex = this.tabTargets.indexOf(tab)

    // Set custom active/inactive classes for tabs
    const activeTabClass = tab.dataset.activeClass || this.activeClassValue
    // For bootstrap compatibility - use non-tailwind classes for inactive state if not specified
    const inactiveTabClass = tab.dataset.inactiveClass || 'inactive'

    // Default panel active/inactive classes
    const activePanelClass = 'tw-block'
    const inactivePanelClass = this.inactiveClassValue

    // Deactivate all tabs
    this.tabTargets.forEach((t, i) => {
      // Remove active classes and add inactive classes
      if (t.dataset.activeClass) {
        t.classList.remove(t.dataset.activeClass)
      } else {
        t.classList.remove(activeTabClass)
      }

      if (t.dataset.inactiveClass) {
        t.classList.add(t.dataset.inactiveClass)
      } else {
        t.classList.add(inactiveTabClass)
      }

      t.setAttribute("aria-selected", "false")
    })

    // Hide all panels
    this.panelTargets.forEach(p => {
      p.classList.remove(activePanelClass)
      p.classList.add(inactivePanelClass)
      p.setAttribute("aria-hidden", "true")
    })

    // Activate selected tab
    if (tab.dataset.activeClass) {
      tab.classList.add(tab.dataset.activeClass)
      tab.classList.remove(tab.dataset.inactiveClass || inactiveTabClass)
    } else {
      tab.classList.add(activeTabClass)
      tab.classList.remove(inactiveTabClass)
    }
    tab.setAttribute("aria-selected", "true")

    // Show corresponding panel
    if (tab.hasAttribute("aria-controls")) {
      // Use aria-controls attribute if available
      const panelId = tab.getAttribute("aria-controls")
      const panel = this.panelTargets.find(p => p.id === panelId)
      if (panel) {
        panel.classList.add(activePanelClass)
        panel.classList.remove(inactivePanelClass)
        panel.setAttribute("aria-hidden", "false")
      }
    } else if (tabIndex >= 0 && tabIndex < this.panelTargets.length) {
      // Otherwise use the matching index
      const panel = this.panelTargets[tabIndex]
      panel.classList.add(activePanelClass)
      panel.classList.remove(inactivePanelClass)
      panel.setAttribute("aria-hidden", "false")
    }

    // Update URL hash if needed
    if (tab.id && tab.hasAttribute("data-tabs-update-url")) {
      history.pushState({}, "", `#${tab.id}`)
    }

    // Store active tab index in localStorage if storageKey is provided
    if (this.hasStorageKeyValue && tabIndex >= 0) {
      setString(this.storageKeyValue, tabIndex.toString())
    }
  }
}